<template>
  <v-row id="cweDetails">
    <v-col cols="12">
      <h4 class="overline mb-1">CWE&trade; Details</h4>
      <v-card flat color="bg_darkerish">
        <v-card-text class="py-6 px-6" v-if="selectedCWE && selectedCWE.id">
          <p class="text-h6 font-weight-light white--text">
            <span class="dw_green--text mr-2">CWE-{{ selectedCWE.id }}</span>
            {{ selectedCWE.name }}
            <v-chip
              label
              outlined
              x-small
              :color="selectedCWE.status === 'Deprecated' ? 'red accent-2': 'grey'"
              class="ml-2 text-uppercase">{{ selectedCWE.status }}</v-chip>
          </p>
          <p class="body-2 font-weight-light">
            {{ selectedCWE.description }}
          </p>
          <p class="body-2 font-weight-light">
            {{ selectedCWE.ext_description }}
          </p>
          <v-divider class="my-2"></v-divider>

          <div v-if="selectedCWE.parent">
            <p class="text-subtitle-1 font-weight-regular white--text mb-0">
              <span>CWE&trade; Parent Weakness</span>
            </p>
            <v-chip
              label
              outlined
              small
              color="dw_green"
              class="my-2"
              @click="$router.push({
                name: 'capec_view',
                params: { id: selectedCWE.parent } })"
            >
              CWE-{{ selectedCWE.parent }}
            </v-chip>
            <v-divider class="my-2"></v-divider>
          </div>

          <div v-if="selectedCWE.next_related">
            <p class="text-subtitle-1 font-weight-regular white--text mb-0">
              <span>Precedes</span>
            </p>
            <v-chip
              label
              outlined
              small
              color="dw_green"
              class="my-2"
              @click="$router.push({
                name: 'cwe_view',
                params: { id: selectedCWE.next_related} })"
            >
              CWE-{{ selectedCWE.next_related }}
            </v-chip>
            <v-divider class="my-2"></v-divider>
          </div>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Weakness Abstraction |
            <span class="font-weight-light grey--text">
              {{ selectedCWE.weakness_abstraction }}
            </span>
          </p>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Weakness Ordinalities
          </p>
          <ul class="font-weight-light">
            <li v-if="selectedCWE.weakness_ordinalities.length === 0">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              --
            </li>
            <div
              v-else
              v-for="(ord, index) in selectedCWE.weakness_ordinalities"
              :key="`ord.${index}`">
              <li>
                <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
                <span class="white--text font-weight-regular">
                  {{ ord[0][1] }}
                </span>
                <span v-if="ord.length > 1" class="ml-1">- {{ ord[1][1] }}</span>
              </li>
            </div>
          </ul>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Applicable Platforms
          </p>
          <ul class="font-weight-light">
            <li v-if="selectedCWE.applicable_platforms.length === 0">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              --
            </li>
            <div
              v-else
              v-for="(plat, index) in selectedCWE.applicable_platforms"
              :key="`plat.${index}`">
              <li>
                <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
                <span class="white--text text-capitalize font-weight-regular mr-1">
                  {{ plat[0][0].toLowerCase() }}
                </span>- {{ plat[0][1] }}
              </li>
              <li :class="index < selectedCWE.applicable_platforms.length - 1 ? 'pb-4' : ''">
                <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
                <span class="white--text text-capitalize font-weight-regular mr-1">
                  {{ plat[1][0].toLowerCase() }}
                </span>- {{ plat[1][1] }}
              </li>
            </div>
          </ul>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Background Details
          </p>
          <!-- TODO: is this text or an array, need an example -->
          <ul class="font-weight-light">
            <li v-if="selectedCWE.background_details.length === 0">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              --
            </li>
            <div
              v-else
              v-for="(bgdeet, index) in selectedCWE.background_details"
              :key="`bgdeet.${index}`">
              <li>
                <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
                {{ bgdeet }}
              </li>
            </div>
          </ul>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Alternate Terms
          </p>
          <ul class="font-weight-light">
            <li v-if="selectedCWE.alternate_terms.length === 0">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              --
            </li>
            <div
              v-else
              v-for="(alt, index) in selectedCWE.alternate_terms"
              :key="`alt.${index}`">
              <li>
                <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
                <span class="white--text font-weight-regular">
                  {{ alt[0][1] }}
                </span>
                <span v-if="alt.length > 1" class="ml-1">- {{ alt[1][1] }}</span>
              </li>
            </div>
          </ul>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Modes of Introduction
          </p>
          <ul class="font-weight-light">
            <li v-if="selectedCWE.modes_of_introduction.length === 0">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              --
            </li>
            <div
              v-else
              v-for="(mode, index) in selectedCWE.modes_of_introduction"
              :key="`mode.${index}`">
              <li>
                <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
                <span class="white--text font-weight-regular">
                  {{ mode[0][1] }}
                </span>
                <span v-if="mode.length > 1" class="ml-1">- {{ mode[1][1] }}</span>
              </li>
            </div>
          </ul>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Common Consequences
          </p>
          <ul class="font-weight-light">
            <li v-if="commonConsequences.length === 0">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              --
            </li>
            <div
              v-else
              v-for="(consq, index) in commonConsequences"
              :key="`consq.${index}`">
                <p class="mb-0">
                  <span class="white--text text-decoration-underline">{{ consq['IMPACT'] }}</span>
                  <span v-if="consq['NOTE']">- {{ consq['NOTE'] }}</span>
                </p>
                <li
                  v-for="(scope, sindex) in consq.scopes"
                  :key="`consq.${index}.scope.${sindex}`"
                  class="ml-6">
                  <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
                  <span class="font-weight-regular mr-1">
                    Scope
                  </span>
                  - {{ scope }}
                </li>
            </div>
          </ul>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Detection Methods
          </p>
          <ul class="font-weight-light">
            <li v-if="detectionMethods.length === 0">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              --
            </li>
            <div
              v-else
              v-for="(dmeth, index) in detectionMethods"
              :key="`dmeth.${index}`">
                <p class="mb-0">
                  <span class="white--text text-decoration-underline mr-1">
                    {{ dmeth['METHOD'] }}
                  </span>
                  - {{ dmeth['DESCRIPTION'] }}
                </p>
                <li class="ml-6">
                  <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
                  <span class="font-weight-regular mr-1">
                    Effectiveness
                  </span>
                  - {{ dmeth['EFFECTIVENESS'] }}
                </li>
            </div>
          </ul>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Potential Mitigations
          </p>
          <ul class="font-weight-light">
            <li v-if="potentialMitigations.length === 0">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              --
            </li>
            <div
              v-else
              v-for="(pmit, index) in potentialMitigations"
              :key="`pmit.${index}`">
                <p class="mb-0">
                  <span class="white--text text-decoration-underline mr-1">
                    {{ pmit['PHASE'] }}
                  </span>
                  - {{ pmit['STRATEGY'] }}
                </p>
                <p class="mb-0">{{ pmit['DESCRIPTION'] }}</p>
                <li class="ml-6" v-if="pmit['EFFECTIVENESS'] !== undefined">
                  <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
                  <span class="font-weight-regular mr-1">
                    Effectiveness
                  </span>
                  - {{ pmit['EFFECTIVENESS'] }}
                </li>
            </div>
          </ul>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Observed Examples
          </p>
          <ul class="font-weight-light">
            <li v-if="selectedCWE.observed_examples.length === 0">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              --
            </li>
            <div
              v-else
              v-for="(oex, index) in selectedCWE.observed_examples"
              :key="`oex.${index}`">
              <li>
                <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
                <a
                  class="font-weight-regular blue--text"
                  :href="`https://cve.mitre.org/cgi-bin/cvename.cgi?name=${oex[0][1]}`"
                  target="__blank">
                  <!-- TODO: use LINK potentially -->
                  {{ oex[0][1] }}
                </a>
                <span class="ml-1">- {{ oex[1][1] }}</span>
              </li>
            </div>
          </ul>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Functional Areas
          </p>
          <ul class="font-weight-light">
            <li v-if="selectedCWE.functional_areas.length === 0">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              --
            </li>
            <div
              v-else
              v-for="(func, index) in selectedCWE.functional_areas"
              :key="`func.${index}`">
              <li>
                <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
                {{ func }}
              </li>
            </div>
          </ul>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Affected Resources
          </p>
          <ul class="font-weight-light">
            <li v-if="selectedCWE.affected_resources.length === 0">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              --
            </li>
            <div
              v-else
              v-for="(arsc, index) in selectedCWE.affected_resources"
              :key="`arsc.${index}`">
              <li>
                <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
                {{ arsc }}
              </li>
            </div>
          </ul>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Taxonomy Mappings
          </p>
          <ul class="font-weight-light">
            <li v-if="taxonomyMappings.length === 0">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              --
            </li>
            <div
              v-else
              v-for="(tax, index) in taxonomyMappings"
              :key="`taxo.${index}`">
              <li>
                <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
                <span class="white--text text-decoration-underline">
                  {{ tax['TAXONOMY NAME'] }}
                </span>
                <span class="ml-1">-</span>
                <span v-if="tax['ENTRY ID']" class="ml-1">{{ tax['ENTRY ID'] }}</span>
                <span v-if="tax['ENTRY NAME']" class="ml-1">{{ tax['ENTRY NAME'] }}</span>
                <span v-if="tax['MAPPING FIT']" class="ml-1">- {{ tax['MAPPING FIT'] }}</span>
              </li>
            </div>
          </ul>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Notes
          </p>
          <ul class="font-weight-light">
            <li v-if="selectedCWE.notes.length === 0">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              --
            </li>
            <div
              v-else
              v-for="(note, index) in selectedCWE.notes"
              :key="`note.${index}`">
              <li>
                <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
                <span class="white--text font-weight-regular mr-1">
                  {{ note[0][1] }}
                </span>
                - {{ note[1][1] }}
              </li>
            </div>
          </ul>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            <span>Related CAPEC&trade; Attack Patterns</span>
          </p>
          <v-chip-group column>
            <v-chip
              v-if="selectedCWE.related_attack_patterns.length === 0"
              label
              outlined
              small
              color="grey">No Related Attack Patterns</v-chip>
            <v-chip
              v-else
              v-for="capec in selectedCWE.related_attack_patterns"
              :key="`capec.${capec}`"
              label
              outlined
              small
              color="dw_green"
              @click="$router.push({ name: 'capec_view', params: { id: capec } })"
            >
              CAPEC-{{ capec }}
            </v-chip>
          </v-chip-group>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            <span>Related Plays</span>
          </p>
          <v-chip-group column>
            <v-chip
              v-if="!selectedCWE.related_plays || selectedCWE.related_plays.length === 0"
              label
              outlined
              small
              color="grey">No Related Plays</v-chip>
            <v-tooltip
              bottom
              v-else
              v-for="ply in selectedCWE.related_plays"
              :key="`ply.${ply.id}`">
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  label
                  outlined
                  small
                  color="dw_green"
                  v-bind="attrs"
                  v-on="on"
                  @click="$router.push({ name: 'play_view', params: { id: ply.id }})">
                  {{ ply.id }}
                </v-chip>
              </template>
              <span>{{ ply.name }}</span>
            </v-tooltip>
          </v-chip-group>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { mdiSquareMediumOutline } from '@mdi/js';

export default {
  name: 'cweDetails',
  data() {
    return {
      mdiSquareMediumOutline
    }
  },
  watch: {
    '$route.params': {
      async handler(params) {
        if (params.id) {
          await this.fetchCWEByID(params.id);
          this.$vuetify.goTo('#cweDetails');
        }
      },
      immediate: true
    }
  },
  computed: {
    ...mapGetters([
      'selectedCWE'
    ]),
    commonConsequences() {
      return this.selectedCWE.common_consequences.map((consq) => {
        const scopes = [];
        let consequence = {};
        consq.forEach(([key, val]) => {
          if (key === 'SCOPE') scopes.push(val);
          else consequence = { ...consequence, [key]: val };
        });
        return { ...consequence, scopes };
      });
    },
    detectionMethods() {
      return this.selectedCWE.detection_methods.map((dmeth) => Object.fromEntries(dmeth));
    },
    potentialMitigations() {
      return this.selectedCWE.potential_mitigations.map((pmit) => Object.fromEntries(pmit));
    },
    taxonomyMappings() {
      return this.selectedCWE.taxonomy_mappings.map((tax) => Object.fromEntries(tax));
    }
  },
  beforeRouteLeave(to, from, next) {
    if (to.name !== 'cwe_view') {
      this.clearSelected('cwe');
    }
    next();
  },
  methods: {
    ...mapActions([
      'fetchCWEByID'
    ]),
    ...mapMutations([
      'clearSelected'
    ])
  }
};
</script>

<style scoped>
ul {
  padding: 0 0 0 0;
}
li {
  list-style-type: none;
}
</style>
